/**
 * AI Services Configuration Service
 *
 * API client for the AI & Speech services configuration panel.
 */
import apiClient from './apiClient';

// ── Types ──────────────────────────────────────────────────────────────

export interface STTConfig {
  primary_provider: string;
  fallback_provider: string;
  streaming_mode: string;
  language: string;
  pause_detection_ms: number;
}

export interface LLMProviderConfig {
  provider_id: string;
  enabled: boolean;
  api_endpoint: string | null;
  default_model: string | null;
  max_retries: number;
  timeout_ms: number;
}

export interface RoutingEntry {
  service: string;
  primary_provider: string;
  primary_model: string;
  primary_display_name: string;
  fallback_provider: string;
  fallback_model: string;
  fallback_display_name: string;
}

export interface CopilotConfig {
  enabled: boolean;
  retrieval_mode: string;
  max_context_size: number;
  confidence_threshold: number;
  error: string | null;
}

export interface LLMConfig {
  primary_provider: string; // assemblyai, openai, or both
}

export interface ProviderHealthStatus {
  provider: string;
  display_name: string;
  status: 'connected' | 'error' | 'unknown';
  latency_ms: number | null;
  error: string | null;
}

export interface AIConfigResponse {
  stt: STTConfig;
  llm_providers: LLMProviderConfig[];
  routing: RoutingEntry[];
  copilot: CopilotConfig;
  provider_health: ProviderHealthStatus[];
}

export interface ModelInfo {
  id: string;
  display_name: string;
}

export interface ProviderModelsResponse {
  provider: string;
  display_name: string;
  models: ModelInfo[];
}

export interface RoutingUpdateRequest {
  service: string;
  primary_provider?: string;
  primary_model?: string;
  fallback_provider?: string;
  fallback_model?: string;
}

// ── Service ────────────────────────────────────────────────────────────

class AIServicesConfigService {
  private baseUrl = '/api/v1/admin/ai-config';

  /**
   * Get the full AI & Speech services configuration.
   */
  async getConfig(): Promise<AIConfigResponse> {
    const response = await apiClient.get(this.baseUrl);
    return response.data;
  }

  /**
   * Update AI config (partial). Accepts stt, copilot, and/or llm sections.
   */
  async updateConfig(payload: {
    stt?: Partial<STTConfig>;
    copilot?: Partial<CopilotConfig>;
    llm?: Partial<LLMConfig>;
  }): Promise<{ updated: string[]; count: number }> {
    const response = await apiClient.put(this.baseUrl, payload);
    return response.data;
  }

  /**
   * Get the LLM service routing table.
   */
  async getRouting(): Promise<RoutingEntry[]> {
    const response = await apiClient.get(`${this.baseUrl}/routing`);
    return response.data;
  }

  /**
   * Update a single routing entry.
   */
  async updateRouting(req: RoutingUpdateRequest): Promise<{
    service: string;
    primary: { provider: string; model: string };
    fallback: { provider: string; model: string };
  }> {
    const response = await apiClient.put(`${this.baseUrl}/routing`, req);
    return response.data;
  }

  /**
   * Get available models for a provider from the live model registry.
   */
  async getProviderModels(providerId: string): Promise<ProviderModelsResponse> {
    const response = await apiClient.get(`/api/v1/llm/providers/${providerId}/models`);
    return response.data;
  }

  /**
   * Check health of all LLM providers.
   */
  async getProviderHealth(): Promise<ProviderHealthStatus[]> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data;
  }
}

export default new AIServicesConfigService();
